import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import OptionalFilters from './OptionalFilters';
import './nodeConfig.less';
import ReusableModal from './ReusableModal';
import ReusableSelect from './ReusableSelect';
import NodeList from './NodeList';
import OTAUpdateStatus from './OTAUpdateStatus';
import {
 fetchDeviceListData,fetchFirmwareUpgrade, fetchFirmwareData,
} from './nodesHelper';
import FirmwareVersionSelection from './FirmwareVersionSelection';
import { hooks } from 'farmx-redux-core';

type DataRow = {
  id: string | number;
  [key: string]: any;
};

const tableKeysToRender = ['deviceId', 'deviceName', 'deviceIdentifier','deviceType', 'fwStatus', 'currentFwVersion',
  'lastFwVersion', 'updatedAt', 'fwSyncStatus'];
// const renderFilters = ['entity', 'status', 'firmwareType', 'currentFwVersion',
//   'lastFwVersion', 'apply'];
const renderFilters = ['entity', 'deviceType', 'apply'];
const FirmwareTypeOption = ['Application'];
const statusDropdownOptions = ['success', 'pending', 'none', 'error'];
const updateTableKeysToRender = ['deviceId', 'deviceName', 'firmwareType', 'lastFwVersion',
  'currentFwVersion', 'fwSyncStatus'];

const OTAUpdate: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<{ type?: string }>({});
  const [selectedDevices, setSelectedDevices] = useState<DataRow[]>([]);
  const [updateFirmConfig, setUpdateFirmConfig] = useState('{}');
  const [otaUpdateStatus, setOtaUpdateStatus] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [lastUpdatedModal, setLastUpdatedModal] = useState('');
  const [firmwareType, setFirmwareType] = useState<string>(FirmwareTypeOption[0]);
  const [selectedFirmwareId, setSelectedFirmwareId] = useState<number | undefined>();
  const [otaUpdateResponse, setOtaUpdateResponse] = useState<{ succeededNodes: string[]; failedNodes: string[] }>({
    succeededNodes: [],
    failedNodes: [],
  });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [firmwareData, setFirmwareData] = useState<any[]>([]); // Store firmware data
  const [tempCurrentVersion, setTempCurrentVersion] = useState<string>('');
  const [tempLastVersion, setTempLastVersion] = useState<string>('');
  const [deviceList, setDeviceList] = useState<DataRow[]>([]);
  const [showData, setShowData] = useState(false);
  const [entityId, setEntityId] = useState<string | undefined>(undefined);
  const [deviceListLoading, setDeviceListLoading] = useState(false);
  const prevTypeRef = useRef<any>(null);
  
  const { t } = useTranslation();

  const fetchDeviceList = (filters: any) => {
    setDeviceListLoading(true);
    fetchDeviceListData(filters?.type, entityId).then((response) => {
      if (!response || response.length === 0) {
        setDeviceListLoading(false);
        setDeviceList([]);
        return;
      }
      setDeviceList(response);
      const lastUpdateFunction = otaUpdateStatus ? setLastUpdatedModal : setLastUpdated;
      lastUpdateFunction(moment().format('YYYY-MM-DD HH:mm:ss'));
      setDeviceListLoading(false);
    }).catch(() => {
      setDeviceList([]);
      setDeviceListLoading(false);
    });
  }

  // Clear the table data when the device type changes.
  useEffect(() => {
    if (
        prevTypeRef.current !== undefined &&
        selectedFilters?.type !== prevTypeRef.current
      ) {
        setDeviceList([]);
        setSelectedDevices([]);
      }
      prevTypeRef.current = selectedFilters?.type;
    }, [selectedFilters?.type]);

  useEffect(() => {
    setSelectedDevices([]);
    setDeviceList([]);
    setShowData(false); 
  },[entityId]);

  useEffect(() => {
  if (!selectedFilters?.type) return;
  fetchFirmwareData(selectedFilters.type).then((response) => {
    if (response) {
      setFirmwareData(response);
    }
  });
}, [selectedFilters?.type]);

  const onNextClick = () => {
    setUpdateFirmConfig(JSON.stringify(firmwareData)); // Use stored data
    setOpen(true);
  };

  const onFiltersApply = (filters: any) => {
    setSelectedFilters((prev) => {
      const newFilters = {
        ...prev,
        ...filters,
        currentFwVersion: tempCurrentVersion || undefined,
        lastFwVersion: tempLastVersion || undefined,
      };
      fetchDeviceList(newFilters);
      return newFilters;
    });
    setShowData(true);
  };
  const handleFirmwareIdChange = (firmwareId: number | undefined) => {
    setSelectedFirmwareId(firmwareId);
  };

  const onConfigApply = async() => {
    try {
      setIsLoading(true);
  
      // Extract device identifiers
      const deviceIdentifiers = selectedDevices.map((device : any) => device.deviceIdentifier);
  
      // Call API and capture response
      const response = await fetchFirmwareUpgrade(deviceIdentifiers, selectedFirmwareId ?? 0, selectedFilters?.type ?? '');
      if (response?.succeededNodes || response?.failedNodes) {
        setOtaUpdateResponse({
          succeededNodes: response.succeededNodes || [],
          failedNodes: response.failedNodes || [],
        });
      }
      setOtaUpdateStatus(true);
    } catch (error) {
      console.error("Firmware upgrade failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const preparedDeviceList = deviceList.map(device => ({
  ...device,
  updatedAt: device.lastFirmwareApplyDate || device.updatedAt,
  }));


  useEffect(() => {
    setErrorMessage(""); // Reset error when the modal opens
  }, [open]); 

  const handleUpdateClick = () => {
    if (!selectedFirmwareId) {
     return setErrorMessage("Please select the next firmware version.");
    }
    // Clear previous selection after updating
    setSelectedFirmwareId(undefined);
    setErrorMessage(''); // Clear error if valid
    onConfigApply(); // Proceed with the firmware update
  };

  const onOkCancel = () => {
    setOpen(false);
    if (otaUpdateStatus) setOtaUpdateStatus(false);
  };

  function renderModelContent() {
    if (!otaUpdateStatus) {
      return (
        <FirmwareVersionSelection 
         totalDevices={selectedDevices?.length}
         fetchFirmwareVersions={firmwareData}
         deviceType={selectedFilters?.type} 
         onFirmwareIdChange={handleFirmwareIdChange}
         selectDevice={selectedDevices}
         errorMessage={errorMessage}
         versionList={selectedDevices}
         setErrorMessage={setErrorMessage}
        />
      );
    }
    return (
      <OTAUpdateStatus 
        nodeCount={selectedDevices.length}
        nodeType={selectedFilters?.type}
        data={deviceList.filter(d => selectedDevices.some(s => s.deviceId === d.deviceId))}
        filters={selectedFilters}
        succeededNodes={otaUpdateResponse?.succeededNodes}
        failedNodes={otaUpdateResponse?.failedNodes}
      />
    );
  }

  const handleManualRefresh = () => {
    if (!showData) return;
    if (selectedFilters?.type) {
        fetchDeviceList(selectedFilters)
    }
  };

  function refreshButton (lastUpdated : any) {
    return(
        <div className='flex-column gap-10'>
          {lastUpdated && (
            <div className="refresh-btn-last-updated">
              <span>{`${t('Last Updated')}: `}</span>
              <span>{lastUpdated}</span>
            </div>
          )}
          <div className="float-left refresh-btn">
            <Button
              key="submit"
              type="primary"
              onClick={handleManualRefresh}
              icon={<ReloadOutlined />}
            >
              {t('Refresh')}
            </Button>
          </div>
        </div>
    )
  }

  const FailedNodesCondition = otaUpdateResponse?.failedNodes;

  function renderCustomFooter() {
    if (!otaUpdateStatus) {
      return [
        <Button
          key="cancel"
          onClick={() => setOpen(false)}
        >
          {t('Cancel')}
        </Button>,
        <Button
          key="open"
          onClick={() => handleUpdateClick()}
          loading={isLoading}
          type= 'primary'
        >
          {t('Update')}
        </Button>
      ];
    }
    return [
      <div>
        <div className='flex-row justify-content-space-between align-item-baseline'>
        {refreshButton(lastUpdatedModal)}
        <Button
          key="cancel"
          onClick={() => setOpen(false)}
        >
          {t('Cancel')}
        </Button>
        </div>
          <div className= {FailedNodesCondition ? "flex-column" : 'flex-auto'}>
          <h3 className="font-weight-bold"
          style ={{
            textAlign : 'left',
            marginTop : '10px'
          }}>
            {`${t('Ignore Nodes (Incompatibility / Missing Data) ')}: `}
          </h3>
          {FailedNodesCondition?.length > 0  ? (
            <NodeList
              renderItems={updateTableKeysToRender}
              showSearch={false}
              isCheckable={false}
              data={selectedDevices}
              displayRecords={5}
              filters={selectedFilters?.type}
              FilterStatus
              entityId={entityId}
              mode='ota'
            />
          ) : (
              <div className="margin-left-10 font-weight-bold  margin-bottom-10" 
                style={{ textAlign: 'center' }}
              >
                {t('No Ignore nodes')}
              </div>
          )}  
        </div>
      </div>
    ];
  }

  function renderModalTitle() {
    return !otaUpdateStatus
      ? t('Firmware Version Selection')
      : t('OTA Update Status');
  }

  const extraFilters = [
    {
    key: 'firmwareType',
    name: 'firmware Type', 
    element: (
        <ReusableSelect 
        options={FirmwareTypeOption} 
        onChange={setFirmwareType} 
        value={firmwareType}
        defaultValue={firmwareType}
        />
      )
    },
    { 
      key: 'currentFwVersion', 
      name: 'Current Version', 
      element: (
        <Input 
          placeholder='0.0.0' 
          value={tempCurrentVersion}
          onChange={(e) => setTempCurrentVersion(e.target.value)}
        />
      )
    },
    { 
      key: 'lastFwVersion', 
      name: 'Previous Version', 
      element: (
        <Input 
          placeholder='0.0.0' 
          value={tempLastVersion}
          onChange={(e) => setTempLastVersion(e.target.value)}
        />
      )
    }
  ]

  return (
    <div className="single-column-page padded desktop-container-config">
      <h1 className='font-bold flex-row justify-content-center reduce-table-space'>
        {t('OTA Update')}
      </h1>
      <div className="flex-space-between optional-filters-div">
      <OptionalFilters 
        renderItems={renderFilters} 
        onChange={(filters) => {
          setSelectedFilters(filters);
          }}
        onApply={onFiltersApply} 
        extraFilters={extraFilters}     
        statusDropdownOptions={statusDropdownOptions}
        entityId={entityId}
        onEntityChange={setEntityId} 
        loading={deviceListLoading}

      />
      </div>
      <NodeList
        renderItems={tableKeysToRender}
        onChange={(d: any) => setSelectedDevices(d)}
        filters={selectedFilters}
        setFilters={setSelectedFilters}
        displayDeviceType
        FilterStatus
        autoRefresh={15}
        entityId={entityId}
        data={preparedDeviceList}
        mode='ota'
      />
      <div className='flex-row justify-content-space-between margin-top-10'>
          {refreshButton(lastUpdated)}
        <div className="flex-row justify-content-end">
          <Button
            key="submit"
            type="primary"
            disabled={!selectedDevices.length}
            onClick={() => onNextClick()}
            className='fixed-width-100'
          >
            {t('Next')}
          </Button>
        </div>
      </div>

      {open && (
      <ReusableModal
        title={renderModalTitle()}
        renderContent={renderModelContent()}
        customFooter={renderCustomFooter()}
        showModal={open}
        onCancel={() => onOkCancel()}
        onOk={() => onOkCancel()}
        className={otaUpdateStatus && 'ota-update-status-modal'}
      />
      )}
    </div>
  );
};

export default OTAUpdate;